import React from 'react';
import { SearchIcon, TargetIcon, MessageSquareIcon, HandshakeIcon, CheckCircleIcon, BarChart2Icon, ArrowRightIcon, ArrowDownIcon, FileTextIcon, UsersIcon } from 'lucide-react';
export const ProcessSection = () => {
  const steps = [{
    icon: <SearchIcon size={24} data-id="element-309" />,
    title: 'Research & Strategy',
    description: 'We analyze your event, audience, and potential sponsors to develop a targeted strategy.'
  }, {
    icon: <TargetIcon size={24} data-id="element-310" />,
    title: 'Prospecting',
    description: 'Identify and qualify potential sponsors that align with your event and audience.'
  }, {
    icon: <MessageSquareIcon size={24} data-id="element-311" />,
    title: 'Engagement',
    description: 'Multi-channel outreach to initiate conversations with potential sponsors.'
  }, {
    icon: <FileTextIcon size={24} data-id="element-312" />,
    title: 'Proposal Development',
    description: 'Create customized sponsorship proposals that highlight value and ROI.'
  }, {
    icon: <UsersIcon size={24} data-id="element-313" />,
    title: 'Negotiation',
    description: 'Skilled negotiation to secure optimal terms for both sponsors and your event.'
  }, {
    icon: <HandshakeIcon size={24} data-id="element-314" />,
    title: 'Closing',
    description: 'Finalize agreements, manage contracts, and secure commitments.'
  }, {
    icon: <CheckCircleIcon size={24} data-id="element-315" />,
    title: 'Delivery & Activation',
    description: 'Support sponsors with activation planning and execution to maximize impact.'
  }, {
    icon: <BarChart2Icon size={24} data-id="element-316" />,
    title: 'Measurement',
    description: 'Track and report on sponsorship performance metrics and outcomes.'
  }, {
    icon: <MessageSquareIcon size={24} data-id="element-317" />,
    title: 'Follow-up & Renewal',
    description: 'Post-event engagement and planning for future sponsorship opportunities.'
  }];
  return <section id="process" className="py-16 md:py-24 bg-white" data-id="element-318">
      <div className="container mx-auto px-6" data-id="element-319">
        <div className="mb-5" data-id="element-320">
          <span className="text-[#1B2A4A] font-medium inline-flex items-center gap-2 bg-[#f5f5f5] px-4 py-1.5 rounded-full shadow-sm" data-id="element-321">
            <div className="w-6 h-6 bg-[#B87333] rounded-sm" data-id="element-322"></div>
            Process
          </span>
        </div>
        <div className="mb-16" data-id="element-323">
          <h2 className="text-3xl md:text-5xl font-bold text-[#1B2A4A] mb-6" data-id="element-324">
            How It{' '}
            <span className="font-serif italic text-[#B87333]" data-id="element-325">Works</span>
          </h2>
          <p className="text-lg md:text-xl text-[#1B2A4A]/80 max-w-2xl leading-relaxed" data-id="element-326">
            Our systematic approach to sponsorship sales delivers predictable
            results and maximum revenue.
          </p>
        </div>
        {/* Main Process Flowchart */}
        <div className="relative mb-12 md:mb-20" data-id="element-327">
          <div className="flex flex-col items-center" data-id="element-328">
            {/* Flowchart container */}
            <div className="w-full max-w-5xl mx-auto" data-id="element-329">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-330">
                {steps.map((step, index) => <div key={index} className="bg-white border border-gray-100 rounded-lg p-5 md:p-7 shadow-md hover:shadow-lg transition-all h-full relative transform hover:-translate-y-1 duration-300" data-id="element-331">
                    <div className="absolute -top-4 -left-3 w-10 h-10 rounded-full bg-[#B87333] text-[#1B2A4A] flex items-center justify-center text-lg font-bold shadow-md z-10" data-id="element-332">
                      {index + 1}
                    </div>
                    <div className="pl-4" data-id="element-333">
                      <div className="flex items-center mb-4 text-[#1B2A4A]" data-id="element-334">
                        <div className="bg-[#B87333]/20 p-2 rounded-full mr-3" data-id="element-335">
                          {step.icon}
                        </div>
                        <h3 className="text-lg font-bold" data-id="element-336">{step.title}</h3>
                      </div>
                      <p className="text-[#1B2A4A]/70 leading-relaxed" data-id="element-337">
                        {step.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
        {/* Networking Image Section */}
        <div className="mb-12 md:mb-20 bg-[#f5f5f5] rounded-lg overflow-hidden border border-gray-100 shadow-lg" data-id="element-338">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0" data-id="element-339">
            <div className="p-6 md:p-10 flex flex-col justify-center" data-id="element-340">
              <h3 className="text-2xl font-bold mb-5 text-[#1B2A4A]" data-id="element-341">
                Maximize Sponsor Engagement
              </h3>
              <p className="text-[#1B2A4A]/70 mb-6 leading-relaxed" data-id="element-342">
                We create strategic networking opportunities that connect
                sponsors directly with your event attendees, enhancing value and
                fostering meaningful relationships.
              </p>
              <ul className="space-y-3 text-[#1B2A4A]/70" data-id="element-343">
                <li className="flex items-start" data-id="element-344">
                  <span className="text-[#B87333] mr-2 font-bold" data-id="element-345">•</span>
                  <span data-id="element-346">Customized activation spaces</span>
                </li>
                <li className="flex items-start" data-id="element-347">
                  <span className="text-[#B87333] mr-2 font-bold" data-id="element-348">•</span>
                  <span data-id="element-349">Interactive brand experiences</span>
                </li>
                <li className="flex items-start" data-id="element-350">
                  <span className="text-[#B87333] mr-2 font-bold" data-id="element-351">•</span>
                  <span data-id="element-352">Targeted networking opportunities</span>
                </li>
                <li className="flex items-start" data-id="element-353">
                  <span className="text-[#B87333] mr-2 font-bold" data-id="element-354">•</span>
                  <span data-id="element-355">Post-event engagement strategies</span>
                </li>
              </ul>
            </div>
            <div className="h-full min-h-[300px] md:min-h-0" data-id="element-356">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/whfid5476MhLhGKZj5y6tn/20240205_140056.jpg" alt="Conference venue with sponsor branding" className="w-full h-full object-cover" data-id="element-357" />
            </div>
          </div>
        </div>
        {/* Typical Timeline */}
        <div className="bg-[#1B2A4A] text-white p-6 md:p-10 rounded-lg shadow-lg border border-[#1B2A4A]" data-id="element-358">
          <h3 className="text-2xl md:text-3xl font-bold mb-8 md:mb-12 text-center" data-id="element-359">
            Typical Sponsorship{' '}
            <span className="font-serif italic text-[#B87333]" data-id="element-360">Timeline</span>
          </h3>
          <div className="relative" data-id="element-361">
            {/* Connecting lines for desktop */}
            <div className="hidden md:block absolute top-1/2 left-[31%] w-[4%] h-1 bg-white/40 z-0" data-id="element-362"></div>
            <div className="hidden md:block absolute top-1/2 right-[31%] w-[4%] h-1 bg-white/40 z-0" data-id="element-363"></div>
            {/* Connecting lines for mobile */}
            <div className="md:hidden absolute h-12 w-0.5 bg-white/40 left-1/2 transform -translate-x-1/2 top-[33%] z-0" data-id="element-364"></div>
            <div className="md:hidden absolute h-12 w-0.5 bg-white/40 left-1/2 transform -translate-x-1/2 top-[66%] z-0" data-id="element-365"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-y-16 md:gap-x-8 relative z-10" data-id="element-366">
              {/* Phase 1 */}
              <div className="relative" data-id="element-367">
                <div className="bg-[#1B2A4A]/70 p-5 md:p-7 rounded-lg border border-white/20 shadow-md hover:bg-[#1B2A4A]/60 transition-all h-full transform hover:-translate-y-1 duration-300" data-id="element-368">
                  <div className="absolute -top-4 -left-4 w-9 h-9 rounded-full bg-[#B87333] text-[#1B2A4A] flex items-center justify-center text-lg font-bold shadow-lg" data-id="element-369">
                    1
                  </div>
                  <h4 className="text-xl font-bold mb-5 text-white pt-2" data-id="element-370">
                    Pre-Event (4-7 months prior)
                  </h4>
                  <ul className="space-y-3" data-id="element-371">
                    <li className="flex items-start" data-id="element-372">
                      <span className="text-[#B87333] mr-2 mt-0.5 font-bold" data-id="element-373">
                        •
                      </span>
                      <span data-id="element-374">Strategy development</span>
                    </li>
                    <li className="flex items-start" data-id="element-375">
                      <span className="text-[#B87333] mr-2 mt-0.5 font-bold" data-id="element-376">
                        •
                      </span>
                      <span data-id="element-377">Package creation</span>
                    </li>
                    <li className="flex items-start" data-id="element-378">
                      <span className="text-[#B87333] mr-2 mt-0.5 font-bold" data-id="element-379">
                        •
                      </span>
                      <span data-id="element-380">Prospecting & outreach</span>
                    </li>
                    <li className="flex items-start" data-id="element-381">
                      <span className="text-[#B87333] mr-2 mt-0.5 font-bold" data-id="element-382">
                        •
                      </span>
                      <span data-id="element-383">Initial engagement</span>
                    </li>
                    <li className="flex items-start" data-id="element-384">
                      <span className="text-[#B87333] mr-2 mt-0.5 font-bold" data-id="element-385">
                        •
                      </span>
                      <span data-id="element-386">Proposal development</span>
                    </li>
                  </ul>
                </div>
                {/* Arrow for desktop */}
                <div className="hidden md:flex absolute top-1/2 -right-4 transform -translate-y-1/2 z-20" data-id="element-387">
                  <div className="bg-[#B87333]/80 text-white p-2 rounded-full shadow-md hover:bg-[#B87333] transition-colors duration-300" data-id="element-388">
                    <ArrowRightIcon size={24} data-id="element-389" />
                  </div>
                </div>
                {/* Arrow for mobile */}
                <div className="md:hidden flex absolute -bottom-8 left-1/2 transform -translate-x-1/2 z-20" data-id="element-390">
                  <div className="bg-[#B87333]/80 text-white p-2 rounded-full shadow-md hover:bg-[#B87333] transition-colors duration-300" data-id="element-391">
                    <ArrowDownIcon size={24} data-id="element-392" />
                  </div>
                </div>
              </div>
              {/* Phase 2 */}
              <div className="relative" data-id="element-393">
                <div className="bg-[#1B2A4A]/70 p-5 md:p-7 rounded-lg border border-white/20 shadow-md hover:bg-[#1B2A4A]/60 transition-all h-full transform hover:-translate-y-1 duration-300" data-id="element-394">
                  <div className="absolute -top-4 -left-4 w-9 h-9 rounded-full bg-[#B87333] text-[#1B2A4A] flex items-center justify-center text-lg font-bold shadow-lg" data-id="element-395">
                    2
                  </div>
                  <h4 className="text-xl font-bold mb-5 text-white pt-2" data-id="element-396">
                    Active Sales (1-3 months prior)
                  </h4>
                  <ul className="space-y-3" data-id="element-397">
                    <li className="flex items-start" data-id="element-398">
                      <span className="text-[#B87333] mr-2 mt-0.5 font-bold" data-id="element-399">
                        •
                      </span>
                      <span data-id="element-400">Negotiation & follow-up</span>
                    </li>
                    <li className="flex items-start" data-id="element-401">
                      <span className="text-[#B87333] mr-2 mt-0.5 font-bold" data-id="element-402">
                        •
                      </span>
                      <span data-id="element-403">Contract finalization</span>
                    </li>
                    <li className="flex items-start" data-id="element-404">
                      <span className="text-[#B87333] mr-2 mt-0.5 font-bold" data-id="element-405">
                        •
                      </span>
                      <span data-id="element-406">Last-minute opportunities</span>
                    </li>
                    <li className="flex items-start" data-id="element-407">
                      <span className="text-[#B87333] mr-2 mt-0.5 font-bold" data-id="element-408">
                        •
                      </span>
                      <span data-id="element-409">Activation planning</span>
                    </li>
                    <li className="flex items-start" data-id="element-410">
                      <span className="text-[#B87333] mr-2 mt-0.5 font-bold" data-id="element-411">
                        •
                      </span>
                      <span data-id="element-412">Sponsor onboarding</span>
                    </li>
                  </ul>
                </div>
                {/* Arrow for desktop */}
                <div className="hidden md:flex absolute top-1/2 -right-4 transform -translate-y-1/2 z-20" data-id="element-413">
                  <div className="bg-[#B87333]/80 text-white p-2 rounded-full shadow-md hover:bg-[#B87333] transition-colors duration-300" data-id="element-414">
                    <ArrowRightIcon size={24} data-id="element-415" />
                  </div>
                </div>
                {/* Arrow for mobile */}
                <div className="md:hidden flex absolute -bottom-8 left-1/2 transform -translate-x-1/2 z-20" data-id="element-416">
                  <div className="bg-[#B87333]/80 text-white p-2 rounded-full shadow-md hover:bg-[#B87333] transition-colors duration-300" data-id="element-417">
                    <ArrowDownIcon size={24} data-id="element-418" />
                  </div>
                </div>
              </div>
              {/* Phase 3 */}
              <div className="relative" data-id="element-419">
                <div className="bg-[#1B2A4A]/70 p-5 md:p-7 rounded-lg border border-white/20 shadow-md hover:bg-[#1B2A4A]/60 transition-all h-full transform hover:-translate-y-1 duration-300" data-id="element-420">
                  <div className="absolute -top-4 -left-4 w-9 h-9 rounded-full bg-[#B87333] text-[#1B2A4A] flex items-center justify-center text-lg font-bold shadow-lg" data-id="element-421">
                    3
                  </div>
                  <h4 className="text-xl font-bold mb-5 text-white pt-2" data-id="element-422">
                    Event & Post-Event
                  </h4>
                  <ul className="space-y-3" data-id="element-423">
                    <li className="flex items-start" data-id="element-424">
                      <span className="text-[#B87333] mr-2 mt-0.5 font-bold" data-id="element-425">
                        •
                      </span>
                      <span data-id="element-426">On-site support</span>
                    </li>
                    <li className="flex items-start" data-id="element-427">
                      <span className="text-[#B87333] mr-2 mt-0.5 font-bold" data-id="element-428">
                        •
                      </span>
                      <span data-id="element-429">Data collection</span>
                    </li>
                    <li className="flex items-start" data-id="element-430">
                      <span className="text-[#B87333] mr-2 mt-0.5 font-bold" data-id="element-431">
                        •
                      </span>
                      <span data-id="element-432">Performance reporting</span>
                    </li>
                    <li className="flex items-start" data-id="element-433">
                      <span className="text-[#B87333] mr-2 mt-0.5 font-bold" data-id="element-434">
                        •
                      </span>
                      <span data-id="element-435">Sponsor feedback</span>
                    </li>
                    <li className="flex items-start" data-id="element-436">
                      <span className="text-[#B87333] mr-2 mt-0.5 font-bold" data-id="element-437">
                        •
                      </span>
                      <span data-id="element-438">Renewal planning</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          {/* Tools section */}
          <div className="mt-16 bg-[#1B2A4A]/50 p-5 md:p-8 rounded-lg border border-white/20 shadow-inner hover:bg-[#1B2A4A]/40 transition-all" data-id="element-439">
            <h4 className="text-xl font-bold mb-5" data-id="element-440">Our Technology Stack</h4>
            <div className="flex flex-wrap gap-3" data-id="element-441">
              {['LinkedIn Sales Navigator', 'Apollo.io', 'Calendly', 'Brevo', 'Odoo', 'Various AI Tools', 'Snov.io', 'LinkedHelper', 'Breakcold'].map((tool, index) => <span key={index} className="bg-white/10 hover:bg-[#B87333]/20 transition-all px-4 py-2 rounded-md border border-white/10 shadow-sm" data-id="element-442">
                  {tool}
                </span>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
};